import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import { fetchReports, promoteReport } from "../../redux/slices/crawler";

function formatTs(value) {
  if (!value) return "—";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value?._seconds
        ? new Date(value._seconds * 1000)
        : typeof value === "string" || typeof value === "number"
          ? new Date(value)
          : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

const UserReports = () => {
  const dispatch = useDispatch();
  const {
    reports,
    reportsLastDocId,
    reportsHasMore,
    reportsLoading,
    promoteLoadingId,
    error,
  } = useSelector((state) => state.crawler);

  useEffect(() => {
    dispatch(fetchReports({ limit: 25 }));
  }, [dispatch]);

  const loadMore = () => {
    if (!reportsLastDocId) return;
    dispatch(fetchReports({ lastDocId: reportsLastDocId, limit: 25 }));
  };

  const onPromote = (id) => {
    if (!id || promoteLoadingId) return;
    dispatch(promoteReport({ id }));
  };

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            User reports
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Catalog feedback from the apps. Promote open reports into the
            review queue as Needs attention change sets.
          </p>
        </section>

        {error && (
          <p className="text-sm text-red-600 mb-3">{String(error)}</p>
        )}

        <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {reportsLoading && !reports.length && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <AiOutlineLoading className="inline h-6 w-6 animate-spin text-purple-600" />
                  </td>
                </tr>
              )}
              {!reports.length && !reportsLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    No user reports yet.
                  </td>
                </tr>
              )}
              {reports.map((item) => {
                const isOpen = !item.status || item.status === "open";
                const promoting = promoteLoadingId === item.id;
                return (
                <tr
                  key={item.id}
                  className="border-t border-gray-100 hover:bg-purple-50/40"
                >
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {formatTs(item.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">
                      {item.status || "open"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {item.entityType || "—"}
                    {item.targetId ? (
                      <code className="block text-xs text-gray-400">
                        {item.targetId}
                      </code>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-800 max-w-md truncate">
                    {item.message || item.reason || item.body || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {item.userId || item.uid || "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {isOpen ? (
                      <Button
                        size="xs"
                        color="purple"
                        onClick={() => onPromote(item.id)}
                        disabled={!!promoteLoadingId}
                        isProcessing={promoting}
                      >
                        Promote
                      </Button>
                    ) : item.changeSetId ? (
                      <Link
                        to={`/review/${item.changeSetId}`}
                        className="text-xs text-purple-700 underline"
                      >
                        Open change set
                      </Link>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {reportsHasMore && (
          <div className="mt-4">
            <Button
              color="purple"
              onClick={loadMore}
              disabled={reportsLoading}
            >
              Load more
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default UserReports;
